'use strict';
/* ==========================================================================
   levels/title.js — the title screen, NOTEBOOK style: the graph paper is the star. On the page's own grid, Jay's
   Fresh Soles price graph draws itself in ink: labelled axes (PAIRS, COST $), three price points pressing in at
   (1, 12), (2, 24), (3, 36), then a ruler line drawn straight through them and on down to the origin, which the
   highlighter circles; "y = 12x" is written beside it. Jay stands beside his "$12 A PAIR" tag. The title is
   handwritten with a highlighter swipe on its last word ("Zero", NB.title from lesson.json), ▶ Play → the map.
   Every point comes from core-local/ratio.js (Ratio.line({p: 12})), never typed in.
   On a phone held upright the controls move to the portrait dock (Dock.title) and the stage keeps only the art.
   Levels.title.loading() draws the loading page (a pencil drawing a line along a ruler) while the boot tier loads.
   Helpers other scenes may reuse: Levels.title.priceTag(text, x, y, o), Levels.title.graph(o).
   ========================================================================== */
(function () {
  const PLAY = { x: 1400, y: 820 };
  const HANDF = NB.FONT.hand;

  /* the title in one or two lines, each sized to fit maxW logical px */
  function titleLines(text, maxW, max) {
    const width = (s, size) => { g.save(); g.font = `700 ${size}px ${HANDF}`; const w = g.measureText(s).width; g.restore(); return w; };
    const words = String(text).split(/\s+/).filter(Boolean);
    let lines = [words.join(' ')];
    if (width(lines[0], max) > maxW && words.length > 1) {
      let best = null;
      for (let k = 1; k < words.length; k++) {
        const a = words.slice(0, k).join(' '), b = words.slice(k).join(' '), d = Math.abs(width(a, 100) - width(b, 100));
        if (!best || d < best.d) best = { d, lines: [a, b] };
      }
      lines = best.lines;
    }
    const size = Math.min(max, ...lines.map(l => max * maxW / Math.max(1, width(l, max))));
    return { lines, size };
  }

  const TAGS = new Map();                   // sprite slots of the price tags (engine spriteIn)
  /* a shop price tag (sale orange, a string hole): x,y = its centre. Cached; o.rot, o.t0 (pops in), o.size */
  function priceTag(text, x, y, o = {}) {
    const size = o.size || 44, t0 = o.t0 ?? -1e9, rot = o.rot ?? -0.12;
    const s = Math.max(0, Math.min(1, (T - t0) / 0.25));
    if (s <= 0) return;
    g.save(); g.font = `700 ${size}px ${NB.FONT.ui}`; const tw = g.measureText(text).width; g.restore();
    const w = tw + size * 1.6, h = size * 1.7;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(E.outBack(s, 1.4), E.outBack(s, 1.4));
    const key = 'tag|' + text + '|' + size;
    let sl = TAGS.get(key); if (!sl) TAGS.set(key, sl = {});
    const fontOk = (() => { try { return document.fonts.check(`700 ${size}px ${NB.FONT.ui}`); } catch (e) { return true; } })();
    const draw = () => {
      const nose = h * 0.45;
      const path = () => { g.beginPath(); g.moveTo(-w / 2, -h / 2 + nose * 0.5); g.lineTo(-w / 2 + nose, -h / 2); g.lineTo(w / 2, -h / 2); g.lineTo(w / 2, h / 2); g.lineTo(-w / 2 + nose, h / 2); g.lineTo(-w / 2, h / 2 - nose * 0.5); g.closePath(); };
      g.save(); g.translate(2, 5); path(); g.fillStyle = 'rgba(29,43,79,0.14)'; g.fill(); g.restore();
      path(); g.fillStyle = NB.PAL.sale; g.fill(); g.lineWidth = 3; g.strokeStyle = NB.PAL.ink; g.stroke();
      g.beginPath(); g.arc(-w / 2 + nose * 0.62, 0, size * 0.14, 0, Math.PI * 2); g.fillStyle = NB.PAL.page; g.fill(); g.stroke();
      g.font = `700 ${size}px ${NB.FONT.ui}`; g.fillStyle = '#fff'; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(text, nose * 0.35, size * 0.04);
    };
    if (!(fontOk && spriteIn(sl, -w / 2 - 6, -h / 2 - 6, w + 14, h + 16, draw))) draw();   // baked once the font is in
    g.restore();
  }

  /* Jay's price graph on the page's own 40 px grid: the origin on a grid crossing, 1 pair = 3 squares, $12 = 3 squares.
     Pure in time: everything keyed to t0. o = {t0, ox, oy} */
  const LINE = Ratio.line({ p: 12 });                                   // y = 12x (DESIGN.md §5.1, s1)
  const PTS = LINE.points([1, 2, 3]);                                   // (1, 12), (2, 24), (3, 36)
  function graph(o = {}) {
    const t0 = o.t0 ?? -1e9, ox = o.ox ?? 400, oy = o.oy ?? 880, sx = 120, sy = 120 / 12, xMax = 4.5, yMax = 54;
    const X = x => ox + x * sx, Y = y => oy - y * sy;
    const ink = NB.PAL.ink, pencil = NB.PAL.pencil;
    // axes (ink, drawn on) with ticks and scale labels
    NB.pen([[ox, oy], [X(xMax) + 20, oy]], { w: 4, color: ink, prog: E.outCubic(P(T, t0, t0 + 0.4)), step: 12 });
    NB.pen([[ox, oy], [ox, Y(yMax) - 20]], { w: 4, color: ink, prog: E.outCubic(P(T, t0 + 0.1, t0 + 0.5)), step: 12 });
    for (let k = 1; k <= 4; k++) {
      const a = t0 + 0.35 + k * 0.06;
      if (T < a) continue;
      g.save(); g.strokeStyle = ink; g.lineWidth = 3;
      g.beginPath(); g.moveTo(X(k), oy - 8); g.lineTo(X(k), oy + 8); g.moveTo(ox - 8, Y(12 * k)); g.lineTo(ox + 8, Y(12 * k)); g.stroke(); g.restore();
      NB.write(String(k), X(k), oy + 34, { size: 32, align: 'center', color: pencil, t0: a });
      NB.write(String(12 * k), ox - 16, Y(12 * k), { size: 32, align: 'right', color: pencil, t0: a });
    }
    NB.write('PAIRS', X(xMax) + 10, oy + 70, { font: 'ui', weight: 700, size: 26, align: 'right', color: pencil, t0: t0 + 0.5 });
    NB.write('COST ($)', ox + 16, Y(yMax) - 24, { font: 'ui', weight: 700, size: 26, color: pencil, t0: t0 + 0.55 });
    // the three price points press in
    const dotAt = [0.8, 1.0, 1.2].map(d => t0 + d);
    PTS.forEach(([x, y], i) => {
      const s = E.outBack(P(T, dotAt[i], dotAt[i] + 0.2), 2);
      if (s <= 0) return;
      g.save(); g.fillStyle = ink; g.beginPath(); g.arc(X(x), Y(y), 11 * s, 0, Math.PI * 2); g.fill(); g.restore();
    });
    // the ruler line: from the last point out, then back through the points and down to the origin
    const lt = t0 + 1.45;
    NB.pen([[X(xMax), Y(LINE.y(xMax))], [ox, oy]], { w: 5, color: ink, prog: E.inOutCubic(P(T, lt, lt + 0.8)), step: 10 });
    // the origin: circled in highlighter, then "(0, 0)" and the equation along the line
    if (T > lt + 0.8) NB.circle(ox, oy, 34, 30, { t0: lt + 0.8, dur: 0.35, color: NB.PAL.hi, w: 16 });
    NB.write('(0, 0)', ox - 30, oy - 48, { size: 36, weight: 700, align: 'right', t0: lt + 1.0 });
    NB.write('y = 12x', X(3.3) + 20, Y(LINE.y(3.3)) + 24, { size: 48, weight: 700, t0: lt + 1.1, rot: -0.02 });
    return { X, Y };
  }

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {};
      NB.ground();
      // the graph draws itself on the left of the page; Jay's tag beside it
      graph({ t0: t0 + 0.2, ox: 360, oy: 880 });
      priceTag('$12 A PAIR', 470, 240, { t0: t0 + 1.2, rot: -0.1, size: 40 });
      // Jay rises in from below on the right, under the title
      const rise = E.outBack(P(T, t0 + 0.6, t0 + 1.1), 1.2);
      Puppet.draw('jay', 1790, 1080 + (1 - rise) * 600, 450, { facing: 0, t: T });
      // the title, handwritten; the highlighter lands on its last word ("Zero")
      const { lines, size } = titleLines(L.title || 'New lesson', 880, 118);
      let y = lines.length > 1 ? 250 : 320;
      lines.forEach((l, i) => {
        const last = i === lines.length - 1 ? l.split(/\s+/).pop() : null;
        NB.title(l, 1400, y, { size, t0: t0 + 0.3 + i * 0.5, hi: last });
        y += size * 1.2;
      });
      NB.write(L.subtitle || '', 1400, y + 20, { font: 'ui', size: 36, color: NB.PAL.pencil, align: 'center', t0: t0 + 1.2, dur: 0.4 });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 420, h: 136, size: 60, primary: true })) this.go();
      if (t > 2.4) NB.write(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x, PLAY.y + 104 + (UI.tap(136) - 136) / 2, { font: 'ui', size: UI.text(30), color: NB.PAL.pencil, align: 'center', t0: t0 + 2.4 });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it
  Title.priceTag = priceTag;
  Title.graph = graph;

  /* the loading page (no assets yet): a pencil drawing a line along a ruler */
  Title.loading = function () { NB.loadingScreen('loading…'); };

  Levels.title = Title;
})();
